import { useRef, useState } from 'react';

import type { ChatAttachment, ChatMessageEntry } from '../../types/chat.types';
import { useSequentialRunner } from '../../hooks/useSequentialRunner';

import { useSparkStatus } from './SparkStatusProvider';

/**
 * useSparkQueue - I own the serial send queue and the list of prompts waiting in it. ONLY
 * concerned with what is waiting to go out - no wire calls, no transcript.
 */
export const useSparkQueue = () => {
  const { setIsLoading } = useSparkStatus();

  const [queuedPrompts, setQueuedPrompts] = useState<ChatMessageEntry[]>([]);

  // For rendering only - `sendQueue.pause` owns the truth.
  const [isQueuePaused, setIsQueuePaused] = useState(false);

  // The `File`s a waiting prompt will send. The queued entry holds only their names, and
  // cancelling drops the task closure that held the only other reference.
  const queuedAttachmentsRef = useRef<Map<string, ChatAttachment[]>>(new Map());

  const editingPromptIdRef = useRef<ChatMessageEntry['id'] | null>(null);

  const [editingPromptId, setEditingPromptId] = useState<
    ChatMessageEntry['id'] | null
  >(null);

  const setEditing = (promptId: ChatMessageEntry['id'] | null) => {
    editingPromptIdRef.current = promptId;
    setEditingPromptId(promptId);
  };

  const sendQueue = useSequentialRunner({
    onDrained: () => {
      setIsLoading(false);
    },
  });

  const stagePrompt = ({
    attachments: staged,
    entry,
    replacePromptId,
  }: {
    attachments: ChatAttachment[];
    entry: ChatMessageEntry;
    replacePromptId?: ChatMessageEntry['id'];
  }) => {
    setQueuedPrompts(previous => {
      const at = replacePromptId
        ? previous.findIndex(queued => queued.id === replacePromptId)
        : -1;

      return at === -1
        ? [...previous, entry]
        : [...previous.slice(0, at), entry, ...previous.slice(at + 1)];
    });

    // A second reference, so a cancelled prompt can hand its files back to the composer -
    // the task's own closure is dropped with it.
    if (staged.length > 0) queuedAttachmentsRef.current.set(entry.id, staged);
  };

  /**
   * The prompt is leaving the queue for the transcript, so the second reference to its files goes.
   * The task's closure still has the one that sends.
   */
  const unstagePrompt = (promptId: ChatMessageEntry['id']) => {
    setQueuedPrompts(previous =>
      previous.filter(queued => queued.id !== promptId)
    );
    queuedAttachmentsRef.current.delete(promptId);
  };

  const resumeQueue = () => {
    sendQueue.resume();
    setIsQueuePaused(false);
  };

  const dismissQueuedPrompt = (promptId: ChatMessageEntry['id']) => {
    if (editingPromptIdRef.current === promptId) {
      setEditing(null);
      resumeQueue();
    } else if (!sendQueue.cancel({ key: promptId })) return;

    // Its file is not coming back either - unlike an edit, which hands it to the composer.
    unstagePrompt(promptId);
  };

  /**
   * Takes a waiting prompt back out for editing: the queue stops where it is, the prompt is
   * cancelled, and its words and files are handed to whoever asked for them.
   *
   * **The handing back is not mine.** Those files belong on the composer's shelf and those words in
   * its field, and neither is queue state - see `SparkSessionUtil.editQueuedPrompt`, which is the
   * one that decides the composer is free to take them.
   */
  const takeQueuedPrompt = (promptId: ChatMessageEntry['id']) => {
    const queued = queuedPrompts.find(prompt => prompt.id === promptId);

    if (!queued) return null;

    if (!sendQueue.cancel({ key: promptId })) return null;

    sendQueue.pause();
    setEditing(promptId);
    setIsQueuePaused(true);

    const staged = queuedAttachmentsRef.current.get(promptId) ?? [];

    queuedAttachmentsRef.current.delete(promptId);

    return { attachments: staged, text: queued.text };
  };

  const clearQueue = () => {
    const dropped = sendQueue.clear();

    setQueuedPrompts([]);
    setEditing(null);
    queuedAttachmentsRef.current.clear();
    resumeQueue();

    return dropped;
  };

  const takeEditing = () => {
    const promptId = editingPromptIdRef.current;

    setEditing(null);

    if (!promptId) return null;

    const index = queuedPrompts.findIndex(queued => queued.id === promptId);

    return { index: index === -1 ? undefined : index, promptId };
  };

  return {
    abandonQueue: clearQueue,
    dismissQueuedPrompt,
    dismissQueuedPrompts: clearQueue,
    editingPromptId,
    takeQueuedPrompt,
    enqueuePrompt: sendQueue.enqueue,
    isQueuePaused,
    queuedPrompts,
    resetQueue: clearQueue,
    resumeQueue,
    stagePrompt,
    takeEditing,
    unstagePrompt,
  };
};
